import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';

const hoy = format(new Date(), 'yyyy-MM-dd');

const DIAS_ENVIO = {
  'lun,mie,vie,sab': [1,3,5,6],
  'lun,vie': [1,5],
};

function proximoDiaEnvio(diasStr) {
  const dias = DIAS_ENVIO[diasStr] || [1,3,5,6];
  const ahora = new Date();
  for (let i = 1; i <= 7; i++) {
    const d = addDays(ahora, i);
    if (dias.includes(d.getDay())) return format(d, 'yyyy-MM-dd');
  }
  return format(addDays(ahora, 1), 'yyyy-MM-dd');
}

const DIAS_SEMANA = [
  { key: 'lun', label: 'Lun' }, { key: 'mar', label: 'Mar' }, { key: 'mie', label: 'Mié' },
  { key: 'jue', label: 'Jue' }, { key: 'vie', label: 'Vie' }, { key: 'sab', label: 'Sáb' }, { key: 'dom', label: 'Dom' },
];

const ESTADO_COLORS = {
  borrador: { bg:'#FEF3C7', color:'#92400E', label:'Borrador' },
  enviado:  { bg:'#D1FAE5', color:'#065F46', label:'Enviado ✓' },
  recibido: { bg:'#DBEAFE', color:'#1E40AF', label:'Recibido ✓' },
};

export default function EnviosHelado() {
  const { usuario } = useAuth();
  const esAdmin = ['gerencia','administracion'].includes(usuario?.rol);
  const esProd  = usuario?.rol === 'produccion';
  const esGerencia = usuario?.rol === 'gerencia';

  const [tab, setTab]             = useState('nuevo');
  const [bodegas, setBodegas]     = useState([]);
  const [sabores, setSabores]     = useState([]);
  const [congeladores, setCongeladores] = useState([]);
  const [bodegaId, setBodegaId]   = useState('');
  const [fecha, setFecha]         = useState(hoy);
  const [detalle, setDetalle]     = useState({});
  const [observacion, setObs]     = useState('');
  const [sugerencia, setSugerencia] = useState(null);
  const [cargandoSug, setCargandoSug] = useState(false);
  const [configForm, setConfigForm] = useState({});
  const [guardandoConfig, setGuardandoConfig] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [envioDetalle, setEnvioDetalle] = useState(null);
  const [ocupacion, setOcupacion] = useState([]);
  const [invPDV, setInvPDV] = useState({});
  const [cargandoInv, setCargandoInv] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get('/bodegas'),
      API.get('/helados/sabores'),
      API.get('/congeladores'),
      API.get('/congeladores/ocupacion'),
    ]).then(([b, s, c, o]) => {
      const pvs = b.data.filter(x => x.id !== 1);
      setBodegas(pvs);
      setSabores(s.data);
      setCongeladores(c.data);
      setOcupacion(o.data);
      if (pvs.length) setBodegaId(String(pvs[0].id));
    }).catch(() => {});
  }, []);

  // Cargar sugerencia al cambiar bodega
  const cargarSugerencia = useCallback(() => {
    if (!bodegaId) return;
    setCargandoSug(true);
    const conf = congeladores.find(c => String(c.bodega_id) === String(bodegaId));
    const fechaEnvio = conf ? proximoDiaEnvio(conf.dias_envio) : hoy;
    API.get(`/envios/sugerencia/${bodegaId}`, { params: { fecha_envio: fechaEnvio } })
      .then(async r => {
        setSugerencia(r.data);
        // Verificar si hay borrador existente para esta bodega/fecha
        const conf = congeladores.find(c => String(c.bodega_id) === String(bodegaId));
        const fechaEnvio = conf ? proximoDiaEnvio(conf.dias_envio) : hoy;
        try {
          const histRes = await API.get('/envios', { params: { bodega_id: bodegaId } });
          const borrador = histRes.data.find(e => e.estado === 'borrador');
          if (borrador) {
            // Cargar detalle del borrador
            const detRes = await API.get(`/envios/${borrador.id}`);
            if (detRes.data.detalle?.length) {
              const d = {};
              detRes.data.detalle.forEach(s => { d[s.sabor_id] = s.bandejas; });
              setDetalle(d);
              return;
            }
          }
        } catch {}
        // Sin borrador — pre-llenar con sugerencia
        if (r.data.sugerencia?.length) {
          const d = {};
          r.data.sugerencia.forEach(s => { d[s.sabor_id] = s.bandejas; });
          setDetalle(d);
        }
      }).catch(() => setSugerencia(null))
      .finally(() => setCargandoSug(false));
  }, [bodegaId, congeladores]);

  useEffect(() => { if (tab === 'nuevo') cargarSugerencia(); }, [bodegaId, tab]);

  // Historial
  const cargarHistorial = () => {
    API.get('/envios', { params: { bodega_id: bodegaId || null } })
      .then(r => setHistorial(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (tab !== 'historial') return;
    cargarHistorial();
  }, [tab, bodegaId]);

  // Inventario PDV — carga todas las bodegas cuando abre el tab
  useEffect(() => {
    if (tab !== 'inventario' || !bodegas.length || !sabores.length) return;
    setCargandoInv(true);
    Promise.all(bodegas.map(b =>
      API.get(`/helados/inventario/${b.id}`).then(r => ({ bodegaId: b.id, data: r.data })).catch(() => ({ bodegaId: b.id, data: null }))
    )).then(results => {
      const mapa = {};
      results.forEach(({ bodegaId, data }) => { mapa[bodegaId] = data; });
      setInvPDV(mapa);
    }).finally(() => setCargandoInv(false));
  }, [tab, bodegas, sabores]);

  const conf = congeladores.find(c => String(c.bodega_id) === String(bodegaId));
  const capacidad = conf?.capacidad_bandejas || 0;
  const maxEnvio  = conf?.max_envio || capacidad;
  const limiteConfigurado = maxEnvio > 0;
  const totalDetalle = Object.values(detalle).reduce((t, v) => t + (parseInt(v)||0), 0);
  const excedeMax = limiteConfigurado && totalDetalle > maxEnvio;
  const ocup = ocupacion.find(o => String(o.bodega_id) === String(bodegaId));
  const bandejasActuales = ocup ? parseInt(ocup.bandejas_actuales) : null;
  const bandejasLibres = bandejasActuales !== null ? capacidad - bandejasActuales : null;

  const handleGuardar = async (enviar = false) => {
    if (!bodegaId) { toast.error('Selecciona un PDV'); return; }
    if (totalDetalle === 0) { toast.error('Agrega al menos un sabor'); return; }
    if (excedeMax) { toast(`⚠️ Superas el máximo sugerido (${maxEnvio} bandejas) — se registrará igual`, { icon: '⚠️' }); }
    setGuardando(true);
    try {
      const det = Object.entries(detalle)
        .filter(([,v]) => parseInt(v) > 0)
        .map(([sabor_id, bandejas]) => ({ sabor_id: parseInt(sabor_id), bandejas: parseInt(bandejas) }));
      await API.post('/envios', { bodega_id: bodegaId, fecha, detalle: det, observacion, enviar });
      toast.success(enviar ? '✅ Envío registrado y confirmado' : '💾 Borrador guardado');
      setDetalle({});
      setObs('');
      cargarSugerencia();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
    finally { setGuardando(false); }
  };

  const verDetalle = async (id) => {
    try {
      const r = await API.get(`/envios/${id}`);
      setEnvioDetalle(r.data);
    } catch { toast.error('Error cargando detalle'); }
  };

  // Configuración de congeladores por PDV (solo gerencia)
  useEffect(() => {
    if (tab !== 'config' || !bodegas.length) return;
    const inicial = {};
    bodegas.forEach(b => {
      const c = congeladores.find(x => String(x.bodega_id) === String(b.id));
      inicial[b.id] = {
        capacidad_bandejas: c?.capacidad_bandejas ?? '',
        tipo_bandeja: c?.tipo_bandeja ?? 'A',
        max_envio: c?.max_envio ?? '',
        dias: c?.dias_envio ? c.dias_envio.split(',') : ['lun','mie','vie','sab'],
      };
    });
    setConfigForm(inicial);
  }, [tab, bodegas, congeladores]);

  const setConfigCampo = (bodegaId, campo, valor) => {
    setConfigForm(f => ({ ...f, [bodegaId]: { ...f[bodegaId], [campo]: valor } }));
  };

  const toggleDia = (bodegaId, dia) => {
    setConfigForm(f => {
      const dias = f[bodegaId]?.dias || [];
      const nuevos = dias.includes(dia) ? dias.filter(d => d !== dia) : [...dias, dia];
      return { ...f, [bodegaId]: { ...f[bodegaId], dias: nuevos } };
    });
  };

  const guardarConfig = async (bodegaId) => {
    const f = configForm[bodegaId];
    if (!f?.capacidad_bandejas || parseInt(f.capacidad_bandejas) <= 0) {
      toast.error('La capacidad debe ser mayor a 0'); return;
    }
    setGuardandoConfig(g => ({ ...g, [bodegaId]: true }));
    try {
      await API.put(`/congeladores/${bodegaId}`, {
        capacidad_bandejas: parseInt(f.capacidad_bandejas),
        tipo_bandeja: f.tipo_bandeja,
        max_envio: f.max_envio ? parseInt(f.max_envio) : null,
        dias_envio: (f.dias || []).join(','),
      });
      toast.success('✅ Configuración guardada');
      const r = await API.get('/congeladores');
      setCongeladores(r.data);
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setGuardandoConfig(g => ({ ...g, [bodegaId]: false })); }
  };

  const bodegaConf = bodegas.find(b => String(b.id) === String(bodegaId));
  const fechaEnvioSugerida = conf ? proximoDiaEnvio(conf.dias_envio) : null;

  return (
    <div style={{ fontFamily:"'Segoe UI',sans-serif", maxWidth:900, margin:'0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <h1 style={{ margin:0, fontSize:26, fontWeight:800, color:'#111827' }}>🚚 Envíos de Helado</h1>
        <p style={{ margin:'4px 0 0', color:'#6B7280', fontSize:14 }}>Registro de despachos desde producción a puntos de venta</p>
      </div>

      {/* Controles superiores */}
      <div style={{ background:'white', borderRadius:14, padding:'16px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', marginBottom:18, display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
        <div>
          <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Punto de venta</div>
          <select value={bodegaId} onChange={e => setBodegaId(e.target.value)}
            style={{ padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', background:'white' }}>
            {bodegas.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Fecha envío</div>
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
            style={{ padding:'8px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none' }} />
        </div>
        {/* Info congelador */}
        {conf && (
          <div style={{ marginLeft:'auto', background:'#F0F9FF', border:'1px solid #BAE6FD', borderRadius:10, padding:'8px 14px', fontSize:12 }}>
            <div style={{ fontWeight:700, color:'#0369A1', marginBottom:4 }}>🧊 Congelador {bodegaConf?.nombre}</div>
            {/* Barra de capacidad */}
            {bandejasActuales !== null && (
              <div style={{ marginBottom:6 }}>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:2 }}>
                  <span style={{ color:'#0C4A6E' }}>Actual: <strong style={{ color: bandejasLibres < 0 ? '#DC2626' : '#0369A1' }}>{bandejasActuales} bandejas</strong></span>
                  <span style={{ color:'#6B7280' }}>Máx: <strong>{capacidad}</strong></span>
                </div>
                <div style={{ height:8, background:'#E0F2FE', borderRadius:4, overflow:'hidden' }}>
                  <div style={{
                    height:'100%',
                    width: `${Math.min(100, (bandejasActuales/capacidad)*100)}%`,
                    background: bandejasActuales/capacidad > 0.85 ? '#EF4444' : bandejasActuales/capacidad > 0.6 ? '#F59E0B' : '#10B981',
                    borderRadius:4, transition:'width 0.3s'
                  }} />
                </div>
                <div style={{ fontSize:11, marginTop:2, color: bandejasLibres <= 0 ? '#DC2626' : '#065F46', fontWeight:700 }}>
                  {bandejasLibres <= 0 ? '⚠️ Sin espacio disponible' : `${bandejasLibres} bandejas libres`}
                </div>
              </div>
            )}
            {bandejasActuales === null && (
              <div style={{ color:'#6B7280', fontSize:11, marginBottom:4 }}>Sin inventario reciente</div>
            )}
            <div style={{ color:'#0C4A6E' }}>
              Capacidad máx: <strong>{conf.capacidad_bandejas}</strong> · tipo {conf.tipo_bandeja}
              {conf.max_envio && <span> · Máx. envío: <strong>{conf.max_envio}</strong></span>}
            </div>
            <div style={{ color:'#0C4A6E' }}>
              Días: <strong>{conf.dias_envio?.replace(/,/g,' · ')}</strong>
            </div>
          </div>
        )}
        {/* Tabs */}
        <div style={{ display:'flex', background:'#F3F4F6', borderRadius:8, padding:2 }}>
          {[
            {id:'nuevo',label:'📦 Nuevo envío'},
            {id:'historial',label:'📋 Historial'},
            {id:'inventario',label:'🧊 Inventario PDV'},
            ...(esGerencia ? [{id:'config',label:'⚙️ Configurar'}] : []),
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ padding:'6px 14px', border:'none', borderRadius:7, cursor:'pointer', fontSize:13,
              fontWeight:tab===t.id?700:500, background:tab===t.id?'white':'transparent', color:tab===t.id?'#111827':'#6B7280',
              boxShadow:tab===t.id?'0 1px 3px rgba(0,0,0,0.1)':'none' }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'historial' ? (
        /* ── HISTORIAL ── */
        <>
          {envioDetalle ? (
            <div style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ background:'#1a1a2e', padding:'14px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ color:'white', fontWeight:800, fontSize:15 }}>🚚 Envío #{envioDetalle.id} — {envioDetalle.bodega_nombre}</div>
                  <div style={{ color:'rgba(255,255,255,0.6)', fontSize:12, marginTop:2 }}>
                    {envioDetalle.fecha ? format(new Date(envioDetalle.fecha.slice(0,10)+'T12:00'),"d 'de' MMMM yyyy",{locale:es}) : '—'} · Registrado por {envioDetalle.registrado_por_nombre}
                  </div>
                </div>
                <button onClick={() => setEnvioDetalle(null)} style={{ background:'rgba(255,255,255,0.15)', border:'none', color:'white', borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:12, fontWeight:700 }}>
                  ← Volver
                </button>
              </div>
              <div style={{ padding:20 }}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10, marginBottom:16 }}>
                  {envioDetalle.detalle?.map(d => (
                    <div key={d.sabor_id} style={{ background:'#F9FAFB', borderRadius:10, padding:'12px 14px', textAlign:'center' }}>
                      <div style={{ fontWeight:800, fontSize:22, color:'#1a1a2e' }}>{d.bandejas}</div>
                      <div style={{ fontSize:12, color:'#6B7280', marginTop:2 }}>bandejas</div>
                      <div style={{ fontWeight:700, fontSize:13, color:'#111827', marginTop:4 }}>{d.sabor_nombre}</div>
                    </div>
                  ))}
                </div>
                <div style={{ background:'#F0FDF4', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#065F46', fontWeight:600 }}>
                  Total: {envioDetalle.detalle?.reduce((t,d) => t+d.bandejas,0)} bandejas enviadas
                  {envioDetalle.observacion && <span> · Obs: {envioDetalle.observacion}</span>}
                </div>
                {envioDetalle.estado === 'borrador' && (
                  <div style={{ marginTop:14, display:'flex', gap:10 }}>
                    <button
                      onClick={async () => {
                        try {
                          await API.post('/envios', {
                            bodega_id: envioDetalle.bodega_id,
                            fecha: envioDetalle.fecha,
                            detalle: envioDetalle.detalle.map(d => ({ sabor_id: d.sabor_id, bandejas: d.bandejas })),
                            observacion: envioDetalle.observacion,
                            enviar: true,
                          });
                          toast.success('✅ Envío confirmado');
                          setEnvioDetalle(null);
                          cargarHistorial();
                        } catch { toast.error('Error al confirmar envío'); }
                      }}
                      style={{ flex:1, padding:'10px', border:'none', borderRadius:10, background:'linear-gradient(135deg,#059669,#047857)', color:'white', cursor:'pointer', fontSize:14, fontWeight:700 }}
                    >
                      ✅ Confirmar y enviar
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ padding:'14px 20px', borderBottom:'1px solid #F3F4F6', fontWeight:800, fontSize:15 }}>📋 Historial de envíos</div>
              {historial.length === 0 ? (
                <div style={{ padding:32, textAlign:'center', color:'#9CA3AF' }}>Sin envíos registrados</div>
              ) : (
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'#F9FAFB' }}>
                      {['Fecha','PDV','Bandejas','Estado','Registrado por',''].map(h => (
                        <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {historial.map((h, i) => {
                      const ec = ESTADO_COLORS[h.estado] || ESTADO_COLORS.borrador;
                      return (
                        <tr key={h.id} style={{ borderTop:'1px solid #F3F4F6', background:i%2===0?'white':'#FAFAFA' }}>
                          <td style={{ padding:'10px 14px', fontWeight:700 }}>{h.fecha ? format(new Date(h.fecha.slice(0,10)+'T12:00'),"d MMM yyyy",{locale:es}) : '—'}</td>
                          <td style={{ padding:'10px 14px' }}>{h.bodega_nombre}</td>
                          <td style={{ padding:'10px 14px', fontFamily:'monospace', fontWeight:800, fontSize:15 }}>{h.total_bandejas || 0}</td>
                          <td style={{ padding:'10px 14px' }}><span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, background:ec.bg, color:ec.color, fontWeight:700 }}>{ec.label}</span></td>
                          <td style={{ padding:'10px 14px', color:'#6B7280' }}>{h.registrado_por_nombre || '—'}</td>
                          <td style={{ padding:'10px 14px' }}>
                            <button onClick={() => verDetalle(h.id)} style={{ padding:'4px 10px', border:'1px solid #E5E7EB', borderRadius:6, background:'white', cursor:'pointer', fontSize:12, fontWeight:600 }}>
                              Ver
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      ) : (
        /* ── NUEVO ENVÍO ── */
        <>
          {/* Card sugerencia */}
          {cargandoSug ? (
            <div style={{ background:'white', borderRadius:14, padding:24, textAlign:'center', color:'#9CA3AF', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', marginBottom:16 }}>
              Calculando sugerencia...
            </div>
          ) : sugerencia && !sugerencia.sin_inventario ? (
            <div style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', marginBottom:16 }}>
              <div style={{ background:'linear-gradient(135deg,#1a1a2e,#16213e)', padding:'12px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ color:'white', fontWeight:800, fontSize:14 }}>💡 Sugerencia de envío</div>
                  <div style={{ color:'rgba(255,255,255,0.6)', fontSize:12, marginTop:2 }}>
                    Basado en inventario del {sugerencia.inventario_fecha ? format(new Date(String(sugerencia.inventario_fecha).slice(0,10)+'T12:00'),"d 'de' MMM",{locale:es}) : '—'} · Próximo envío: <strong style={{color:'white'}}>{fechaEnvioSugerida ? format(new Date(fechaEnvioSugerida+'T12:00'),"EEEE d 'de' MMM",{locale:es}) : '—'}</strong>
                  </div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ color:'white', fontWeight:800, fontSize:22 }}>{sugerencia.bandejas_a_enviar}</div>
                  <div style={{ color:'rgba(255,255,255,0.6)', fontSize:11 }}>bandejas sugeridas</div>
                </div>
              </div>
              <div style={{ padding:'12px 20px', background:'#F8FAFC', display:'flex', gap:12, flexWrap:'wrap' }}>
                <div style={{ fontSize:12, color:'#374151' }}>🧊 Ocupadas: <strong>{sugerencia.bandejas_ocupadas}</strong></div>
                <div style={{ fontSize:12, color:'#374151' }}>📦 Espacio libre: <strong>{sugerencia.espacio_libre}</strong></div>
                <div style={{ fontSize:12, color:'#374151' }}>✅ Capacidad total: <strong>{sugerencia.congelador?.capacidad_bandejas}</strong></div>
                {sugerencia.congelador?.max_envio && (
                  <div style={{ fontSize:12, color:'#F59E0B', fontWeight:700 }}>⚠️ Máx por envío: {sugerencia.congelador.max_envio}</div>
                )}
              </div>
              {/* Sugerencia por sabor */}
              {sugerencia.sugerencia?.length > 0 && (
                <div style={{ padding:'12px 20px', display:'flex', gap:8, flexWrap:'wrap', borderTop:'1px solid #F3F4F6' }}>
                  {sugerencia.sugerencia.map(s => (
                    <div key={s.sabor_id} style={{ background:'#EEF2FF', borderRadius:8, padding:'6px 12px', fontSize:12, fontWeight:700, color:'#4338CA' }}>
                      {s.nombre}: {s.bandejas} 🧊
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : sugerencia?.sin_inventario ? (
            <div style={{ background:'#FEF3C7', border:'1px solid #FDE68A', borderRadius:12, padding:'12px 18px', marginBottom:16, fontSize:13, color:'#92400E', fontWeight:600 }}>
              ⚠️ Este PDV aún no tiene inventario enviado. No se puede calcular sugerencia.
            </div>
          ) : null}

          {/* Formulario de envío */}
          <div style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ background:'#1a1a2e', padding:'12px 20px' }}>
              <div style={{ color:'white', fontWeight:800, fontSize:14 }}>📦 Detalle del envío</div>
              <div style={{ color:'rgba(255,255,255,0.5)', fontSize:12, marginTop:2 }}>Ajusta las cantidades según lo que se enviará realmente</div>
            </div>

            {/* Barra de progreso bandejas */}
            <div style={{ padding:'14px 20px', background:'#F8FAFC', borderBottom:'1px solid #F3F4F6' }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:13 }}>
                <span style={{ fontWeight:700 }}>Bandejas a enviar: <span style={{ color: excedeMax ? '#EF4444' : '#10B981', fontSize:16 }}>{totalDetalle}</span></span>
                <span style={{ color:'#6B7280' }}>{limiteConfigurado ? `Máx. sugerido: ${maxEnvio}` : 'Sin límite configurado'}</span>
              </div>
              {limiteConfigurado && (
                <div style={{ height:8, background:'#E5E7EB', borderRadius:4, overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${Math.min(totalDetalle/maxEnvio*100,100)}%`,
                    background: excedeMax ? '#EF4444' : totalDetalle/maxEnvio > 0.85 ? '#F59E0B' : '#10B981',
                    borderRadius:4, transition:'width 0.2s' }} />
                </div>
              )}
              {excedeMax && (
                <div style={{ marginTop:6, fontSize:12, color:'#B45309', background:'#FFFBEB', padding:'6px 10px', borderRadius:6, fontWeight:600 }}>
                  ⚠️ Superas el máximo sugerido para este PDV — es solo una referencia, puedes continuar.
                </div>
              )}
            </div>

            {/* Grid sabores */}
            <div style={{ padding:20, display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))', gap:12 }}>
              {sabores.map(s => (
                <div key={s.id} style={{ border:'2px solid', borderColor: (detalle[s.id]||0) > 0 ? '#6366F1' : '#E5E7EB', borderRadius:12, padding:'12px 14px', transition:'border-color 0.15s' }}>
                  <div style={{ fontWeight:700, fontSize:13, marginBottom:8, color:'#111827' }}>{s.nombre}</div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <button onClick={() => setDetalle(p => ({ ...p, [s.id]: Math.max(0,(parseInt(p[s.id])||0)-1) }))}
                      style={{ width:28, height:28, border:'2px solid #E5E7EB', borderRadius:6, background:'white', cursor:'pointer', fontSize:16, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }}>−</button>
                    <input type="number" min="0" value={detalle[s.id] ?? ''}
                      onChange={e => setDetalle(p => ({ ...p, [s.id]: e.target.value===''?0:parseInt(e.target.value)||0 }))}
                      style={{ flex:1, textAlign:'center', padding:'5px', border:'2px solid #E5E7EB', borderRadius:6, fontSize:16, fontWeight:800, outline:'none', width:0 }} />
                    <button onClick={() => setDetalle(p => ({ ...p, [s.id]: (parseInt(p[s.id])||0)+1 }))}
                      style={{ width:28, height:28, border:'2px solid #E5E7EB', borderRadius:6, background:'white', cursor:'pointer', fontSize:16, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }}>+</button>
                  </div>
                  {(detalle[s.id]||0) > 0 && (
                    <div style={{ marginTop:6, textAlign:'center', fontSize:11, color:'#6366F1', fontWeight:700 }}>
                      {detalle[s.id]} bandeja{detalle[s.id]>1?'s':''}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Observación */}
            <div style={{ padding:'0 20px 16px' }}>
              <textarea value={observacion} onChange={e => setObs(e.target.value)} placeholder="Observación (opcional)..."
                rows={2} style={{ width:'100%', padding:'10px 12px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', resize:'vertical', boxSizing:'border-box', fontFamily:'inherit' }} />
            </div>

            {/* Botones */}
            <div style={{ padding:'0 20px 20px', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => handleGuardar(false)} disabled={guardando}
                style={{ padding:'10px 22px', border:'2px solid #E5E7EB', borderRadius:10, background:'white', cursor:'pointer', fontSize:14, fontWeight:700, color:'#374151' }}>
                {guardando ? '⏳...' : '💾 Guardar borrador'}
              </button>
              <button onClick={() => handleGuardar(true)} disabled={guardando || totalDetalle === 0}
                style={{ padding:'10px 26px', border:'none', borderRadius:10,
                  background: totalDetalle===0 ? '#E5E7EB' : excedeMax ? '#F59E0B' : 'linear-gradient(135deg,#1a1a2e,#16213e)',
                  color: totalDetalle===0 ? '#9CA3AF' : 'white',
                  cursor: totalDetalle===0 ? 'not-allowed' : 'pointer', fontSize:14, fontWeight:700 }}>
                {guardando ? '⏳...' : excedeMax ? '⚠️ Confirmar de todos modos' : '✅ Confirmar envío'}
              </button>
            </div>
          </div>
        </>
      )}

      {tab === 'inventario' && (
        /* ── INVENTARIO PDV ── */
        <div>
          {cargandoInv ? (
            <div style={{ textAlign:'center', padding:'40px', color:'#6B7280' }}>Cargando inventarios...</div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:16 }}>
              {bodegas.map(b => {
                const inv = invPDV[b.id];
                const conf_b = congeladores.find(c => String(c.bodega_id) === String(b.id));
                const ocup_b = ocupacion.find(o => String(o.bodega_id) === String(b.id));
                const actuales = ocup_b ? parseInt(ocup_b.bandejas_actuales) : null;
                const capMax = conf_b?.capacidad_bandejas || 0;
                const libres = actuales !== null ? capMax - actuales : null;
                const pct = actuales !== null && capMax > 0 ? Math.min(100, (actuales/capMax)*100) : 0;
                const barColor = pct > 85 ? '#EF4444' : pct > 60 ? '#F59E0B' : '#10B981';
                return (
                  <div key={b.id} style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.08)' }}>
                    {/* Header bodega */}
                    <div style={{ background:'#1a1a2e', padding:'12px 16px' }}>
                      <div style={{ color:'white', fontWeight:800, fontSize:14 }}>🏪 {b.nombre}</div>
                      {actuales !== null ? (
                        <div style={{ marginTop:6 }}>
                          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'rgba(255,255,255,0.7)', marginBottom:3 }}>
                            <span>{actuales} bandejas en congelador</span>
                            <span>Máx {capMax}</span>
                          </div>
                          <div style={{ height:6, background:'rgba(255,255,255,0.2)', borderRadius:3, overflow:'hidden' }}>
                            <div style={{ height:'100%', width:`${pct}%`, background:barColor, borderRadius:3 }} />
                          </div>
                          <div style={{ fontSize:11, marginTop:3, color: libres <= 0 ? '#FCA5A5' : '#86EFAC', fontWeight:700 }}>
                            {libres <= 0 ? '⚠️ Congelador lleno' : `${libres} bandejas libres`}
                          </div>
                        </div>
                      ) : (
                        <div style={{ color:'rgba(255,255,255,0.5)', fontSize:11, marginTop:4 }}>Sin inventario registrado</div>
                      )}
                    </div>
                    {/* Sabores */}
                    <div style={{ padding:'10px 14px' }}>
                      {inv?.inventario?.length ? (
                        <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                          {inv.inventario.filter(i => i.bandejas_congelador > 0 || i.nivel_bandeja > 0).map(item => {
                            const s = sabores.find(x => String(x.id) === String(item.sabor_id));
                            const niveles = ['', '⅓', '⅔', '🟢'];
                            return (
                              <div key={item.sabor_id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:12, padding:'4px 0', borderBottom:'1px solid #F3F4F6' }}>
                                <span style={{ fontWeight:600, color:'#374151' }}>{s?.nombre || `Sabor ${item.sabor_id}`}</span>
                                <div style={{ display:'flex', gap:10, color:'#6B7280' }}>
                                  {item.nivel_bandeja > 0 && <span title="En vitrina">🍦 {niveles[item.nivel_bandeja]}</span>}
                                  {item.bandejas_congelador > 0 && <span title="En congelador">🧊 {item.bandejas_congelador}</span>}
                                </div>
                              </div>
                            );
                          })}
                          {inv.inventario.filter(i => i.bandejas_congelador > 0 || i.nivel_bandeja > 0).length === 0 && (
                            <div style={{ color:'#9CA3AF', fontSize:12, textAlign:'center', padding:'8px 0' }}>Todo en cero</div>
                          )}
                        </div>
                      ) : (
                        <div style={{ color:'#9CA3AF', fontSize:12, textAlign:'center', padding:'12px 0' }}>Sin inventario</div>
                      )}
                      {inv?.fecha_registro && (
                        <div style={{ fontSize:10, color:'#9CA3AF', marginTop:8, textAlign:'right' }}>
                          Actualizado: {format(new Date(inv.fecha_registro+'T12:00'), "d MMM HH:mm", {locale:es})}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {tab === 'config' && esGerencia && (
        /* ── CONFIGURACIÓN DE CONGELADORES (solo gerencia) ── */
        <div>
          <div style={{ background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:10, padding:'10px 16px', marginBottom:16, fontSize:12, color:'#1E40AF' }}>
            ℹ️ La capacidad y el máximo de envío son <strong>referencias</strong> para producción — al confirmar un envío que las supera, solo se muestra una advertencia, no se bloquea.
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:16 }}>
            {bodegas.map(b => {
              const f = configForm[b.id] || {};
              const tieneConf = congeladores.some(c => String(c.bodega_id) === String(b.id));
              return (
                <div key={b.id} style={{ background:'white', borderRadius:14, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.08)' }}>
                  <div style={{ background:'#1a1a2e', padding:'10px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ color:'white', fontWeight:800, fontSize:14 }}>🏪 {b.nombre}</span>
                    {!tieneConf && <span style={{ background:'#FEF3C7', color:'#92400E', fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20 }}>Sin configurar</span>}
                  </div>
                  <div style={{ padding:16, display:'flex', flexDirection:'column', gap:12 }}>
                    <div style={{ display:'flex', gap:10 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Capacidad (bandejas) *</div>
                        <input type="number" min="1" value={f.capacidad_bandejas ?? ''} onChange={e => setConfigCampo(b.id, 'capacidad_bandejas', e.target.value)}
                          placeholder="Ej: 46" style={{ width:'100%', padding:'8px 10px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} />
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>Tipo bandeja</div>
                        <div style={{ display:'flex', gap:6 }}>
                          {['A','B'].map(t => (
                            <button key={t} onClick={() => setConfigCampo(b.id, 'tipo_bandeja', t)}
                              style={{ flex:1, padding:'8px 0', border:`2px solid ${f.tipo_bandeja===t?'#1a1a2e':'#E5E7EB'}`, borderRadius:8,
                                background:f.tipo_bandeja===t?'#1a1a2e':'white', color:f.tipo_bandeja===t?'white':'#374151', cursor:'pointer', fontSize:13, fontWeight:700 }}>
                              {t}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:4 }}>
                        Máx. envío sugerido <span style={{ fontWeight:400, textTransform:'none', color:'#9CA3AF' }}>(opcional — si se deja vacío usa la capacidad)</span>
                      </div>
                      <input type="number" min="1" value={f.max_envio ?? ''} onChange={e => setConfigCampo(b.id, 'max_envio', e.target.value)}
                        placeholder={f.capacidad_bandejas ? `Ej: ${f.capacidad_bandejas}` : 'Ej: 32'} style={{ width:'100%', padding:'8px 10px', border:'2px solid #E5E7EB', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }} />
                    </div>

                    <div>
                      <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', marginBottom:6 }}>Días de envío</div>
                      <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                        {DIAS_SEMANA.map(d => {
                          const activo = (f.dias || []).includes(d.key);
                          return (
                            <button key={d.key} onClick={() => toggleDia(b.id, d.key)}
                              style={{ padding:'6px 10px', border:`2px solid ${activo?'#1a1a2e':'#E5E7EB'}`, borderRadius:8,
                                background:activo?'#1a1a2e':'white', color:activo?'white':'#6B7280', cursor:'pointer', fontSize:11, fontWeight:700 }}>
                              {d.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <button onClick={() => guardarConfig(b.id)} disabled={guardandoConfig[b.id]}
                      style={{ padding:'9px 0', border:'none', borderRadius:8, background:'#1a1a2e', color:'white', cursor:'pointer', fontSize:13, fontWeight:700, marginTop:4 }}>
                      {guardandoConfig[b.id] ? '⏳ Guardando...' : '💾 Guardar'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
